import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Badge, Tabs, Tab, InputGroup } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';

const LoginModal = ({ show, onHide }) => {
  const { login, register } = useAuth();
  const [activeTab, setActiveTab] = useState('login');

  // Login form state
  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [showLoginPw, setShowLoginPw] = useState(false);

  // Register form state
  const [regUsername, setRegUsername] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirm, setRegConfirm] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regError, setRegError] = useState('');
  const [regSuccess, setRegSuccess] = useState('');
  const [regLoading, setRegLoading] = useState(false);
  const [showRegPw, setShowRegPw] = useState(false);
  const [showRegConfirm, setShowRegConfirm] = useState(false);

  const resetForms = () => {
    setLoginUsername(''); setLoginPassword(''); setLoginError('');
    setShowLoginPw(false);
    setRegUsername(''); setRegPassword(''); setRegConfirm(''); setRegEmail('');
    setRegError(''); setRegSuccess('');
    setShowRegPw(false); setShowRegConfirm(false);
    setActiveTab('login');
  };

  const handleClose = () => {
    resetForms();
    onHide();
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    if (!loginUsername || !loginPassword) {
      setLoginError('Please enter username and password.');
      return;
    }
    setLoginLoading(true);
    try {
      await login(loginUsername, loginPassword);
      handleClose();
    } catch (err) {
      setLoginError(err.message || 'Login failed.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegError(''); setRegSuccess('');
    if (!regUsername || !regPassword) {
      setRegError('Username and password are required.');
      return;
    }
    if (regPassword !== regConfirm) {
      setRegError('Passwords do not match.');
      return;
    }
    if (regPassword.length < 8) {
      setRegError('Password must be at least 8 characters.');
      return;
    }
    setRegLoading(true);
    try {
      await register(regUsername, regPassword, regEmail);
      setRegSuccess('Account created! You can now log in.');
      setRegUsername(''); setRegPassword(''); setRegConfirm(''); setRegEmail('');
      setTimeout(() => setActiveTab('login'), 1500);
    } catch (err) {
      setRegError(err.message || 'Registration failed.');
    } finally {
      setRegLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fas fa-user-lock me-2"></i>
          Account
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-3">
          {/* ── Login tab ── */}
          <Tab eventKey="login" title="Login">
            <Form onSubmit={handleLogin}>
              {loginError && <Alert variant="danger" className="py-2">{loginError}</Alert>}
              <Form.Group className="mb-3">
                <Form.Label>Username</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter username"
                  value={loginUsername}
                  onChange={e => setLoginUsername(e.target.value)}
                  autoComplete="username"
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Password</Form.Label>
                <InputGroup>
                  <Form.Control
                    type={showLoginPw ? 'text' : 'password'}
                    placeholder="Enter password"
                    value={loginPassword}
                    onChange={e => setLoginPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                  />
                  <Button
                    variant="outline-secondary"
                    tabIndex={-1}
                    onClick={() => setShowLoginPw(v => !v)}
                    aria-label={showLoginPw ? 'Hide password' : 'Show password'}
                  >
                    <i className={`fas ${showLoginPw ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </Button>
                </InputGroup>
              </Form.Group>
              <Button type="submit" variant="primary" className="w-100" disabled={loginLoading}>
                {loginLoading
                  ? <><i className="fas fa-spinner fa-spin me-2"></i>Logging in…</>
                  : <><i className="fas fa-sign-in-alt me-2"></i>Login</>
                }
              </Button>
            </Form>
            <hr className="my-3" />
            <p className="text-muted text-center small mb-0">
              Need an account?{' '}
              <Button variant="link" className="p-0 small" onClick={() => setActiveTab('register')}>
                Register
              </Button>
            </p>
          </Tab>

          {/* ── Register tab ── */}
          <Tab eventKey="register" title="Register">
            <Form onSubmit={handleRegister}>
              {regError && <Alert variant="danger" className="py-2">{regError}</Alert>}
              {regSuccess && <Alert variant="success" className="py-2">{regSuccess}</Alert>}
              <Form.Group className="mb-3">
                <Form.Label>Username <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Choose a username"
                  value={regUsername}
                  onChange={e => setRegUsername(e.target.value)}
                  autoComplete="username"
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Email <span className="text-muted">(optional)</span></Form.Label>
                <Form.Control
                  type="email"
                  placeholder="your@email.com"
                  value={regEmail}
                  onChange={e => setRegEmail(e.target.value)}
                  autoComplete="email"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Password <span className="text-danger">*</span></Form.Label>
                <InputGroup>
                  <Form.Control
                    type={showRegPw ? 'text' : 'password'}
                    placeholder="At least 8 characters"
                    value={regPassword}
                    onChange={e => setRegPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                  <Button
                    variant="outline-secondary"
                    tabIndex={-1}
                    onClick={() => setShowRegPw(v => !v)}
                    aria-label={showRegPw ? 'Hide password' : 'Show password'}
                  >
                    <i className={`fas ${showRegPw ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </Button>
                </InputGroup>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Confirm password <span className="text-danger">*</span></Form.Label>
                <InputGroup>
                  <Form.Control
                    type={showRegConfirm ? 'text' : 'password'}
                    placeholder="Repeat password"
                    value={regConfirm}
                    onChange={e => setRegConfirm(e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                  <Button
                    variant="outline-secondary"
                    tabIndex={-1}
                    onClick={() => setShowRegConfirm(v => !v)}
                    aria-label={showRegConfirm ? 'Hide password' : 'Show password'}
                  >
                    <i className={`fas ${showRegConfirm ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </Button>
                </InputGroup>
              </Form.Group>
              <Button type="submit" variant="success" className="w-100" disabled={regLoading}>
                {regLoading
                  ? <><i className="fas fa-spinner fa-spin me-2"></i>Creating account…</>
                  : <><i className="fas fa-user-plus me-2"></i>Create account</>
                }
              </Button>
            </Form>
            <p className="text-muted text-center small mt-3 mb-0">
              Regular accounts can save chat history. Admin access must be granted by an administrator.
            </p>
          </Tab>
        </Tabs>
      </Modal.Body>
    </Modal>
  );
};

export default LoginModal;
